import { useState, useEffect } from 'react';
import { Menu, X, Phone, MapPin, ArrowRight, Building2, Layers, Leaf, Mail } from 'lucide-react';
import logoNav from '@/assets/logo-canaa-nav.svg';
import Magnetic from './ui/Magnetic';

const navLinks = [
  { href: '#quem-somos',       label: 'Quem Somos',      icon: Building2 },
  { href: '#segmentos',        label: 'Segmentos',        icon: Layers    },
  { href: '#sustentabilidade', label: 'Sustentabilidade', icon: Leaf      },
  { href: '#contato',          label: 'Contato',          icon: Mail      },
];

export const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-[999] w-full flex flex-col">

      {/* Barra de info — desliza para cima ao rolar */}
      <div
        className={`w-full bg-cinza-escuro text-cinza-claro text-xs md:text-sm px-6 flex justify-center border-b border-white/10 shadow-sm overflow-hidden transition-all duration-300 ease-out ${
          scrolled ? 'max-h-0 opacity-0 py-0' : 'max-h-[40px] opacity-100'
        }`}
      >
        <div className="max-w-[1400px] w-full flex justify-between items-center py-2">
          <div className="flex items-center gap-2 font-semibold whitespace-nowrap">
            <Phone className="w-4 h-4 text-destaque-2" />
            (37) 3242-9992
          </div>
          <div className="hidden md:flex items-center gap-2 opacity-90 tracking-wide whitespace-nowrap">
            <MapPin className="w-4 h-4 text-destaque-2" />
            Itaúna, Minas Gerais
          </div>
        </div>
      </div>

      {/* Nav pill */}
      <div className="w-full px-4 pt-4 flex justify-center">
        <nav className="relative flex items-center justify-between w-full max-w-[1400px] h-[72px] px-6 md:px-10 rounded-full bg-branco shadow-lg border border-cinza-claro">

          <a href="/" className="flex items-center">
            <img src={logoNav} alt="Tubos Canaã" className="h-12 md:h-14 w-auto" width={180} height={56} />
          </a>

          <div className="hidden lg:flex gap-8 items-center absolute left-1/2 -translate-x-1/2">
            {navLinks.map(({ href, label, icon: Icon }) => (
              <a
                key={href}
                href={href}
                className="relative flex items-center text-sm font-bold text-cinza-escuro/80 hover:text-primary transition-colors duration-300 py-2 px-1 group"
              >
                <span className="flex items-center justify-center overflow-hidden text-primary transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] w-0 opacity-0 scale-50 mr-0 group-hover:w-5 group-hover:opacity-100 group-hover:scale-100 group-hover:mr-2">
                  <Icon className="w-5 h-5" />
                </span>
                <span className="relative">
                  {label}
                  <span className="absolute -bottom-1 left-0 h-0.5 bg-primary w-0 transition-all duration-300 ease-out group-hover:w-full" />
                </span>
              </a>
            ))}
          </div>

          <div className="hidden lg:flex">
              <a
                href="#contato"
                className="relative flex items-center justify-between h-11 pl-6 pr-1 rounded-full overflow-hidden group bg-branco border border-cinza-claro shadow-sm cursor-pointer"
              >
                <span className="absolute top-0 right-0 bottom-0 w-11 bg-primary rounded-full transition-all duration-[600ms] ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:w-full z-0" />
                <span className="relative z-10 text-[0.75rem] font-black uppercase tracking-widest text-cinza-escuro group-hover:text-branco transition-colors duration-300 mr-3">
                  Solicitar Orçamento
                </span>
                <div className="relative z-10 w-9 h-9 rounded-full flex items-center justify-center text-branco group-hover:translate-x-1 transition-transform duration-300">
                  <ArrowRight className="w-[18px] h-[18px]" strokeWidth={3} />
                </div>
              </a>
          </div>

          <button
            type="button"
            className="lg:hidden text-cinza-escuro"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
          >
            {mobileOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </nav>
      </div>

      {/* Menu mobile */}
      <div
        className={`lg:hidden mx-4 mt-2 bg-branco rounded-2xl shadow-xl border border-cinza-claro overflow-hidden transition-all duration-300 ease-out ${
          mobileOpen ? 'max-h-[400px] opacity-100 pointer-events-auto' : 'max-h-0 opacity-0 pointer-events-none'
        }`}
      >
        <div className="flex flex-col gap-1 p-6">
          {navLinks.map(({ href, label, icon: Icon }) => (
            <a
              key={href}
              href={href}
              className="flex items-center gap-3 text-base font-bold text-cinza-escuro/80 hover:text-primary transition-colors py-2.5 border-b border-cinza-claro last:border-0"
              onClick={() => setMobileOpen(false)}
            >
              <Icon className="w-5 h-5 text-primary" />
              {label}
            </a>
          ))}
          <a
            href="#orcamento"
            className="mt-3 flex items-center justify-center gap-2 h-11 bg-primary text-branco rounded-full font-black text-xs uppercase tracking-widest"
            onClick={() => setMobileOpen(false)}
          >
            Solicitar Orçamento
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </header>
  );
};
